/**
 * Noise Extraction catalog — single Unity-compatible balance.csv + encounters.json.
 *
 * Ownership (parallel-safe):
 * - GAMEPLAY keys (`hold*`, `scrape*`, `pursuer*`, loot/noise, `interactHoldSec`, …)
 *   → consumed by `noise-extraction-rules.ts` (alert / hold / AI SSOT).
 * - PRESENTATION keys (`sonarTip*`, `sonarMemory*`, `sonarLight*`, `sonarAgeMul`,
 *   `echoFadeSec`, pool caps, fog memory, memory/terrain paint fade, camera display)
 *   → consumed by `view/*` + scene draw/pools only.
 * Do not put alert/hold SSOT in the scene. Tip speed ≠ life burn (`sonarTipSpeedMul` vs `sonarAgeMul`).
 */
import balanceCsv from "./tables/noise-extraction/balance.csv?raw";
import encounterDoc from "./tables/noise-extraction/encounters.json";
import { csvToKeyValue, num } from "./loaders/parse-csv";

export type LootTier = "low" | "high";

export type NoiseBalance = {
  // --- gameplay ---
  playerBaseSpeed: number;
  playerRadius: number;
  /** Shared loot/exit hold duration. */
  interactHoldSec: number;
  extractRadius: number;
  lootInteractRadius: number;
  holdPulsePeriodSec: number;
  holdSonarMaxRange: number;
  holdSonarRayCount: number;
  holdSonarScale: number;
  /** Wall-clock life mul on hold isotropic PlayerSonar (presentation consumes via event). */
  holdSonarLifeMul: number;
  noiseDecayPerSec: number;
  noiseMax: number;
  lootLowValue: number;
  lootLowNoise: number;
  lootHighValue: number;
  lootHighNoise: number;
  carrySpeedMulMin: number;
  carrySpeedMulPerValue: number;
  pursuerSpeedByAlert: readonly [number, number, number, number];
  pursuerRadius: number;
  pursuerCatchRadius: number;
  pursuerCatchDwellSec: number;
  pursuerWanderSpeed: number;
  pursuerVisionRadius: number;
  pursuerInvestigateSpeedMul: number;
  /** lastHeard → trailDir × this (first sweep waypoint). */
  pursuitSweepForwardPx: number;
  /** ±perpendicular offset from forward sweep point. */
  pursuitSweepSidePx: number;
  /** How many predicted waypoints to check (2–3). */
  pursuitSweepCount: number;
  pursuitSweepArrivePx: number;
  pursuitSweepSpeedMul: number;
  hearRangeBase: number;
  hearRangePerIntensity: number;
  hearRangePerAlert: number;
  hearMinIntensity: number;
  heardErrorMin: number;
  heardErrorPerDist: number;
  heardErrorQuietBonus: number;
  heardMemorySec: number;
  heardUpdateCooldownSec: number;
  heardAngleJitter: number;
  heardDistJitter: number;
  heardForwardBias: number;
  noiseRadiusPerUnit: number;
  lootBurstRadiusMul: number;
  pulseFadeSec: number;
  footstepNoisePerPx: number;
  footstepIntervalPx: number;
  footstepPulseNoise: number;
  scrapeNoise: number;
  scrapeImpactMinGoalDist: number;
  scrapeSonarLifeMul: number;
  /** Scrape/collision PlayerSonar maxRange multiplier (stride uses sonarMaxRange as-is). */
  scrapeSonarMaxRangeMul: number;
  scrapeScalePerGoalPx: number;
  scrapeScaleMin: number;
  scrapeScaleMax: number;
  scrapeCooldownSec: number;
  sonarOrange: number;
  blockStallClearSec: number;
  sonarPulsePeriodSec: number;
  /**
   * Rare backup while moving (footstep stride is the primary move sonar).
   * Kept slow so long walks aren't a metronome from the body center.
   */
  sonarMovePulsePeriodSec: number;
  /**
   * Cast move sonar every N footsteps (`Math.max(1, floor(n))`).
   * 0.5→1 = each step with L/R footSide alternate (bipedal stride).
   */
  sonarFootstepEvery: number;
  /** Lateral origin sway from facing (px) — L/R feet. */
  sonarFootOffsetPx: number;
  sonarMaxRange: number;
  sonarRayCount: number;
  /** Shared stride maxRange mul (player Footstep + pursuer PursuerSonar). */
  sonarStrideRangeMul: number;
  /** Shared stride rayCount mul (player + pursuer). */
  sonarStrideRayMul: number;
  noiseLobeSideAmp: number;
  noiseLobeForwardPower: number;
  idleSonarScale: number;
  /**
   * Heartbeat presence pulse period (sec) — player + each pursuer while in play.
   * Independent of movement (unlike footstep stride).
   */
  heartbeatIntervalSec: number;
  /** Absolute maxRange px — keep ≪ stride (`sonarMaxRange * sonarStrideRangeMul`). */
  heartbeatSonarMaxRange: number;
  /** Cast amp scale (1 = full soft ring; softness mainly via lifeMul + rail). */
  heartbeatSonarScale: number;
  /** Soft ring residual life mul (&lt;1) — thinner/shorter than stride. */
  heartbeatSonarLifeMul: number;
  heartbeatSonarRayCount: number;
  echoFadeSec: number;
  /** Life burn multiplier (2 = twice as fast). Couples beam + contact expiry. */
  sonarAgeMul: number;
  /**
   * Tip travel only (1.5 = outbound/reflect tip 1.5× current).
   * Does not shorten wall-clock life (unlike raising sonarAgeMul alone).
   */
  sonarTipSpeedMul: number;
  revealStampSec: number;
  sonarBeamPoolCap: number;
  sonarContactPoolCap: number;
  fogMemoryAlpha: number;
  fogMemoryHalfWidth: number;
  /**
   * Residual rail afterimage age rate (RT fill toward memoryPaintFadeColor).
   * Higher = leftover wave chroma flattens faster.
   */
  memoryPaintFadePerSec: number;
  /**
   * Tip-kiss terrain/loot/exit silhouette age rate (slower than residual).
   * Keeps wall cutouts readable when looking back.
   */
  terrainPaintFadePerSec: number;
  /** Planar fade target — typically camera void black. */
  memoryPaintFadeColor: number;
  /** Primary player sonar light line color. */
  sonarLightBlue: number;
  /**
   * Search-mode pursuer live rail — low-chroma slate/violet (not danger red).
   * Investigate/sweep/chase use `sonarLightRed`.
   */
  sonarLightPursuer: number;
  /** Reacting pursuer (investigate/sweep/chase/catching) live rail. */
  sonarLightRed: number;
  /** Aged blue afterimage (memory trail). */
  sonarMemoryBlue: number;
  sonarMemoryBlueSoft: number;
  /** Pursuer aged red afterimage (legacy; live paint uses memoryPaintFromRail). */
  sonarMemoryRed: number;
  sonarMemoryRedSoft: number;
  /** Player-center red halo on AlertChanged 0→1 (presentation). */
  sonarAlertHaloSec: number;
  sonarAlertHaloRadius: number;
  /** Chase/catching pursuer stride residual life mul (>1 = longer afterglow). */
  sonarChaseLifeMul: number;
  /** Soft smear when pursuer sonar kisses the player (not GPS). */
  sonarContactErrorPx: number;
  /** Same grid cell won't re-pulse until this cooldown (standing spam). */
  sonarSameCellCooldownSec: number;
  sonarCellSize: number;
  sonarFacingBins: number;
  cameraFollowLerp: number;
  displayFollowHz: number;
  displaySnapPx: number;
  map: { xMin: number; xMax: number; yMin: number; yMax: number };
};

export type NoiseEncounterLoot = {
  id: string;
  tier: LootTier;
  x: number;
  y: number;
};

export type NoiseEncounter = {
  id: string;
  seed: number;
  playerSpawn: { x: number; y: number };
  exit: { x: number; y: number };
  /** Tracker spawn points (2+). */
  pursuerSpawns: readonly { x: number; y: number }[];
  loot: readonly NoiseEncounterLoot[];
  blocks: readonly { x: number; y: number; w: number; h: number }[];
};

function buildBalance(): NoiseBalance {
  const m = csvToKeyValue(balanceCsv);
  return {
    playerBaseSpeed: num(m, "playerBaseSpeed", 160),
    playerRadius: num(m, "playerRadius", 14),
    interactHoldSec: num(m, "interactHoldSec", 1),
    extractRadius: num(m, "extractRadius", 40),
    lootInteractRadius: num(m, "lootInteractRadius", 22),
    holdPulsePeriodSec: num(m, "holdPulsePeriodSec", 0.25),
    holdSonarMaxRange: num(m, "holdSonarMaxRange", 1680),
    holdSonarRayCount: num(m, "holdSonarRayCount", 48),
    holdSonarScale: num(m, "holdSonarScale", 1.85),
    holdSonarLifeMul: num(m, "holdSonarLifeMul", 2),
    noiseDecayPerSec: num(m, "noiseDecayPerSec", 10),
    noiseMax: num(m, "noiseMax", 120),
    lootLowValue: num(m, "lootLowValue", 10),
    lootLowNoise: num(m, "lootLowNoise", 18),
    lootHighValue: num(m, "lootHighValue", 32),
    lootHighNoise: num(m, "lootHighNoise", 52),
    carrySpeedMulMin: num(m, "carrySpeedMulMin", 0.55),
    carrySpeedMulPerValue: num(m, "carrySpeedMulPerValue", 0.0075),
    pursuerSpeedByAlert: [
      num(m, "pursuerSpeed0", 40),
      num(m, "pursuerSpeed1", 85),
      num(m, "pursuerSpeed2", 100),
      num(m, "pursuerSpeed3", 135),
    ],
    pursuerRadius: num(m, "pursuerRadius", 16),
    pursuerCatchRadius: num(m, "pursuerCatchRadius", 30),
    pursuerCatchDwellSec: num(m, "pursuerCatchDwellSec", 0.5),
    pursuerWanderSpeed: num(m, "pursuerWanderSpeed", 32),
    pursuerVisionRadius: num(m, "pursuerVisionRadius", 90),
    pursuerInvestigateSpeedMul: num(m, "pursuerInvestigateSpeedMul", 1),
    pursuitSweepForwardPx: num(m, "pursuitSweepForwardPx", 150),
    pursuitSweepSidePx: num(m, "pursuitSweepSidePx", 90),
    pursuitSweepCount: num(m, "pursuitSweepCount", 2),
    pursuitSweepArrivePx: num(m, "pursuitSweepArrivePx", 26),
    pursuitSweepSpeedMul: num(m, "pursuitSweepSpeedMul", 0.9),
    hearRangeBase: num(m, "hearRangeBase", 140),
    hearRangePerIntensity: num(m, "hearRangePerIntensity", 6.5),
    hearRangePerAlert: num(m, "hearRangePerAlert", 28),
    hearMinIntensity: num(m, "hearMinIntensity", 10),
    heardErrorMin: num(m, "heardErrorMin", 90),
    heardErrorPerDist: num(m, "heardErrorPerDist", 0.42),
    heardErrorQuietBonus: num(m, "heardErrorQuietBonus", 110),
    heardMemorySec: num(m, "heardMemorySec", 4.5),
    heardUpdateCooldownSec: num(m, "heardUpdateCooldownSec", 0.85),
    heardAngleJitter: num(m, "heardAngleJitter", 1.2),
    heardDistJitter: num(m, "heardDistJitter", 70),
    heardForwardBias: num(m, "heardForwardBias", 0.55),
    noiseRadiusPerUnit: num(m, "noiseRadiusPerUnit", 1.35),
    lootBurstRadiusMul: num(m, "lootBurstRadiusMul", 1.85),
    pulseFadeSec: num(m, "pulseFadeSec", 0.65),
    footstepNoisePerPx: num(m, "footstepNoisePerPx", 0.12),
    footstepIntervalPx: num(m, "footstepIntervalPx", 36),
    footstepPulseNoise: num(m, "footstepPulseNoise", 4),
    scrapeNoise: num(m, "scrapeNoise", 8),
    scrapeImpactMinGoalDist: num(m, "scrapeImpactMinGoalDist", 80),
    scrapeSonarLifeMul: num(m, "scrapeSonarLifeMul", 6.5),
    scrapeSonarMaxRangeMul: num(m, "scrapeSonarMaxRangeMul", 3),
    scrapeScalePerGoalPx: num(m, "scrapeScalePerGoalPx", 0.0025),
    scrapeScaleMin: num(m, "scrapeScaleMin", 0.6),
    scrapeScaleMax: num(m, "scrapeScaleMax", 2.4),
    scrapeCooldownSec: num(m, "scrapeCooldownSec", 0.55),
    sonarOrange: num(m, "sonarOrange", 0xf97316),
    blockStallClearSec: num(m, "blockStallClearSec", 0.35),
    sonarPulsePeriodSec: num(m, "sonarPulsePeriodSec", 0.55),
    sonarMovePulsePeriodSec: num(m, "sonarMovePulsePeriodSec", 1.1),
    sonarFootstepEvery: num(m, "sonarFootstepEvery", 0.5),
    sonarFootOffsetPx: num(m, "sonarFootOffsetPx", 26),
    sonarMaxRange: num(m, "sonarMaxRange", 440),
    sonarRayCount: num(m, "sonarRayCount", 28),
    sonarStrideRangeMul: num(m, "sonarStrideRangeMul", 0.95),
    sonarStrideRayMul: num(m, "sonarStrideRayMul", 0.9),
    noiseLobeSideAmp: num(m, "noiseLobeSideAmp", 0.3),
    noiseLobeForwardPower: num(m, "noiseLobeForwardPower", 2.2),
    idleSonarScale: num(m, "idleSonarScale", 1),
    heartbeatIntervalSec: num(m, "heartbeatIntervalSec", 0.9),
    heartbeatSonarMaxRange: num(m, "heartbeatSonarMaxRange", 105),
    heartbeatSonarScale: num(m, "heartbeatSonarScale", 1),
    heartbeatSonarLifeMul: num(m, "heartbeatSonarLifeMul", 0.35),
    heartbeatSonarRayCount: num(m, "heartbeatSonarRayCount", 16),
    echoFadeSec: num(m, "echoFadeSec", 3.2),
    sonarAgeMul: num(m, "sonarAgeMul", 2),
    sonarTipSpeedMul: num(m, "sonarTipSpeedMul", 1.5),
    revealStampSec: num(m, "revealStampSec", 4.5),
    sonarBeamPoolCap: num(m, "sonarBeamPoolCap", 80),
    sonarContactPoolCap: num(m, "sonarContactPoolCap", 16),
    fogMemoryAlpha: num(m, "fogMemoryAlpha", 0.35),
    fogMemoryHalfWidth: num(m, "fogMemoryHalfWidth", 4.5),
    memoryPaintFadePerSec: num(m, "memoryPaintFadePerSec", 0.28),
    terrainPaintFadePerSec: num(m, "terrainPaintFadePerSec", 0.04),
    memoryPaintFadeColor: num(m, "memoryPaintFadeColor", 0x05070c),
    sonarLightBlue: num(m, "sonarLightBlue", 0x60a5fa),
    sonarLightPursuer: num(m, "sonarLightPursuer", 0x7a7394),
    sonarLightRed: num(m, "sonarLightRed", 0xf87171),
    sonarMemoryBlue: num(m, "sonarMemoryBlue", 0x3d6a9a),
    sonarMemoryBlueSoft: num(m, "sonarMemoryBlueSoft", 0x243d5c),
    sonarMemoryRed: num(m, "sonarMemoryRed", 0x9a3d3d),
    sonarMemoryRedSoft: num(m, "sonarMemoryRedSoft", 0x5c2424),
    sonarAlertHaloSec: num(m, "sonarAlertHaloSec", 0.4),
    sonarAlertHaloRadius: num(m, "sonarAlertHaloRadius", 52),
    sonarChaseLifeMul: num(m, "sonarChaseLifeMul", 1.45),
    sonarContactErrorPx: num(m, "sonarContactErrorPx", 28),
    sonarSameCellCooldownSec: num(m, "sonarSameCellCooldownSec", 1.35),
    sonarCellSize: num(m, "sonarCellSize", 64),
    sonarFacingBins: num(m, "sonarFacingBins", 8),
    cameraFollowLerp: num(m, "cameraFollowLerp", 0.12),
    displayFollowHz: num(m, "displayFollowHz", 14),
    displaySnapPx: num(m, "displaySnapPx", 48),
    map: {
      xMin: num(m, "mapXMin", 0),
      xMax: num(m, "mapXMax", 2400),
      yMin: num(m, "mapYMin", 0),
      yMax: num(m, "mapYMax", 3600),
    },
  };
}

function buildEncounter(): NoiseEncounter {
  const raw = encounterDoc as NoiseEncounter;
  if (!raw.pursuerSpawns?.length) {
    throw new Error("noise-extraction encounter requires pursuerSpawns[]");
  }
  return {
    id: raw.id,
    seed: raw.seed,
    playerSpawn: { ...raw.playerSpawn },
    exit: { ...raw.exit },
    pursuerSpawns: raw.pursuerSpawns.map((p) => ({ ...p })),
    loot: raw.loot.map((l) => ({ ...l })),
    blocks: raw.blocks.map((b) => ({ ...b })),
  };
}

export const noiseExtractionCatalog = {
  balance: buildBalance(),
  encounter: buildEncounter(),
} as const;

export function lootDef(tier: LootTier): { value: number; noise: number } {
  const b = noiseExtractionCatalog.balance;
  return tier === "high"
    ? { value: b.lootHighValue, noise: b.lootHighNoise }
    : { value: b.lootLowValue, noise: b.lootLowNoise };
}
